// Full updated Profile component with working email popup + validation

import { useState, useEffect } from "react";
import { useAuthContext } from "../context/context";
import Headline from "../Components/Elements/Headline";
import PageContentContainer from "../Components/ContentContainers/PageContentContainer";
import GenericContentCard from "../Components/Cards/CardGenericContent";
import { useLanguage } from "../context/LanguageContext";
import Toast from "../Components/Elements/Toast";
import { useLocation, useNavigate } from "react-router-dom";
import TabPersonalInfo from "../Components/Tabs/TabPersonalInfo";
import TabSubscription from "../Components/Tabs/TabSubscription";
import TabResources from "../Components/Tabs/TabResources";
import TabSecurity from "../Components/Tabs/TabSecurity";
import TabDataPrivacy from "../Components/Tabs/TabDataPrivacy";
import InputSelect from "../Components/Inputs/InputSelect";
import countries from "world-countries";

const countryOptions = countries
  .map((c) => ({ label: c.name.common, value: c.name.common }))
  .sort((a, b) => a.label.localeCompare(b.label));

function Profile() {
  const { userData, setUserData } = useAuthContext();
  const { copy } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("tab1");
  const [firstName, setFirstName] = useState(userData.first_name || "");
  const [surname, setSurname] = useState(userData.surname || "");
  const [country, setCountry] = useState(userData.country || "");
  const [toast, setToast] = useState({ message: "", type: "" });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab === "subscription") {
      setActiveTab("tab2");
    }
  }, [location]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("emailChanged") === "true") {
      setToast({
        message: copy.profile.errors.email_updated,
        type: "success",
      });
    } else if (params.get("error") === "true") {
      setToast({
        message: copy.profile.errors.email_change_failed,
        type: "error",
      });
    }
  }, [location]);

  const handleFieldUpdate = (name, value) => {
    setUserData((prev) => ({ ...prev, [name]: value }));
    if (name === "first_name") setFirstName(value);
    if (name === "surname") setSurname(value);
    if (name === "country") setCountry(value);
  };

  const handleUpdatePersonalDetails = async () => {
    const userId = userData?.id;
    if (!userId)
      return setToast({
        message: copy.profile.errors.user_id_missing,
        type: "error",
      });
    try {
      // You may want to move this API call to TabPersonalInfo for full encapsulation
      const updatedUser = await import("../api").then((api) =>
        api.updateProfilePersonalDetails(userId, {
          first_name: firstName,
          surname,
          country,
        })
      );
      setUserData((prev) => ({ ...prev, ...updatedUser }));
      setToast({
        message: copy.profile.errors.personal_details_updated,
        type: "success",
      });
    } catch {
      setToast({
        message: copy.profile.errors.personal_details_failed,
        type: "error",
      });
    }
  };

  const calculateDaysLeft = (trialEndDate) => {
    if (!trialEndDate) return 0;
    const end = new Date(trialEndDate);
    const today = new Date();
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const personalInfoFields = [
    {
      label: copy.profile.personal.form.label_name,
      name: "first_name",
      value: firstName,
      type: "text",
    },
    {
      label: copy.profile.personal.form.label_surname,
      name: "surname",
      value: surname,
      type: "text",
    },
    {
      label: copy.profile.personal.form.label_country,
      name: "country",
      value: country,
      type: "select",
      component: InputSelect,
      options: countryOptions,
    },
  ];

  return (
    <>
      <PageContentContainer>
        <Headline
          headline={copy.profile.headline}
          description={copy.profile.description}
        />
        <div className="grid grid-cols-1 md:grid-cols-4 md:gap-4 gap-1 w-full md:min-h-[80vh]">
          {/* Mobile Tabs (Horizontal Scroll) */}
          <div className="flex md:hidden flex-row gap-2 mb-4 card-base-border card-base card-base-background p-1.5 overflow-x-auto overflow-y-hidden hide-scrollbar whitespace-nowrap md:px-2 md:py-4">
            <button
              className={`py-2 px-4 cursor-pointer card-base rounded-md card-base-border text-base ${activeTab === "tab1" ? "bg-accent-light " : " hover:shadow-sm "} `}
              onClick={() => setActiveTab("tab1")}
            >
              {copy.profile.tab_1}
            </button>
            <button
              className={`py-2 px-4 cursor-pointer card-base rounded-md card-base-border text-base ${activeTab === "tab2" ? "bg-accent-light " : " hover:shadow-sm "} `}
              onClick={() => setActiveTab("tab2")}
            >
              {copy.profile.tab_2}
            </button>
            <button
              className={`py-2 px-4 cursor-pointer card-base rounded-md card-base-border text-base ${activeTab === "tab3" ? "bg-accent-light " : " hover:shadow-sm "} `}
              onClick={() => setActiveTab("tab3")}
            >
              {copy.profile.tab_3}
            </button>
            <button
              className={`py-2 px-4 cursor-pointer card-base rounded-md card-base-border text-base ${activeTab === "tab4" ? "bg-accent-light " : " hover:shadow-sm "} `}
              onClick={() => setActiveTab("tab4")}
            >
              {copy.profile.tab_4}
            </button>
            <button
              className={`py-2 px-4 cursor-pointer card-base rounded-md card-base-border text-base ${activeTab === "tab5" ? "bg-accent-light " : " hover:shadow-sm "} `}
              onClick={() => setActiveTab("tab5")}
            >
              {copy.profile.tab_6}
            </button>
          </div>

          {/* Desktop Sidebar */}
          <div className="hidden md:flex md:col-span-1 card-base-background flex-col items-start card-base card-base-border overflow-hidden mb-4 md:mb-0">
            <button
              className={`cursor-pointer w-full p-3 hover:bg-accent-light text-left ${activeTab === "tab1" ? "bg-accent-light" : ""}`}
              onClick={() => setActiveTab("tab1")}
            >
              {copy.profile.tab_1}
            </button>
            <button
              className={`cursor-pointer w-full p-3 hover:bg-accent-light text-left ${activeTab === "tab2" ? "bg-accent-light" : ""}`}
              onClick={() => setActiveTab("tab2")}
            >
              {copy.profile.tab_2}
            </button>
            <button
              className={`cursor-pointer w-full p-3 hover:bg-accent-light text-left ${activeTab === "tab3" ? "bg-accent-light" : ""}`}
              onClick={() => setActiveTab("tab3")}
            >
              {copy.profile.tab_3}
            </button>
            <button
              className={`cursor-pointer w-full p-3 hover:bg-accent-light text-left ${activeTab === "tab4" ? "bg-accent-light" : ""}`}
              onClick={() => setActiveTab("tab4")}
            >
              {copy.profile.tab_4}
            </button>
            <button
              className={`cursor-pointer w-full p-3 hover:bg-accent-light text-left ${activeTab === "tab5" ? "bg-accent-light" : ""}`}
              onClick={() => setActiveTab("tab5")}
            >
              {copy.profile.tab_6}
            </button>
          </div>

          {/* Content */}
          <div className="md:col-span-3 relative transition-all duration-300 ease-in-out">
            <GenericContentCard>
              {activeTab === "tab1" && (
                <TabPersonalInfo
                  personalInfoFields={personalInfoFields}
                  handleFieldUpdate={handleFieldUpdate}
                  handleUpdatePersonalDetails={handleUpdatePersonalDetails}
                  setToast={setToast}
                />
              )}
              {activeTab === "tab2" && (
                <TabSubscription
                  navigate={navigate}
                  calculateDaysLeft={calculateDaysLeft}
                />
              )}
              {activeTab === "tab3" && <TabSecurity setToast={setToast} />}
              {activeTab === "tab4" && <TabResources copy={copy} />}
              {activeTab === "tab5" && (
                <TabDataPrivacy copy={copy} setToast={setToast} />
              )}
            </GenericContentCard>
          </div>
        </div>
      </PageContentContainer>
      {toast.message && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast({ message: "", type: "" })}
        />
      )}
    </>
  );
}

export default Profile;
